import { API_URL } from '../config/api';
import { InterviewMessage } from '../types/chat';
import { BaseInterviewService } from '../types/interview';
import * as signalR from '@microsoft/signalr';

export interface ContentPlanFormData {
  topic: string;
  description: string;
  services: string;
  aboutAuthor: string;
  contentPreferences: string;
}

export class ContentPlanInterviewService implements BaseInterviewService {
  private connection: signalR.HubConnection | null = null;
  private messageHandlers: ((message: InterviewMessage) => void)[] = [];
  private statusHandlers: ((status: string) => void)[] = [];
  private completionHandlers: ((extractedData: ContentPlanFormData) => void)[] = [];
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private token: string | null = null;
  private interviewId: string | null = null;
  private STORAGE_KEY = 'content_plan_interview_id';

  constructor(token: string) {
    this.token = token;
    
    // Формируем URL для хаба, убирая /api из API_URL
    const hubUrl = `${API_URL.replace('/api', '')}/contentPlanInterviewHub`;
    
    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => {
          return this.token || '';
        }
      })
      .withAutomaticReconnect()
      .build();

    this.setupConnectionHandlers();
  }

  private setupConnectionHandlers(): void {
    if (!this.connection) return;

    this.connection.on('ReceiveMessage', (messageData: any) => {
      const message: InterviewMessage = {
        id: messageData.Id || messageData.id || Date.now().toString(),
        content: messageData.Content || messageData.content || '',
        isFromAI: messageData.IsFromAI !== undefined ? messageData.IsFromAI : messageData.isFromAI || false,
        timestamp: new Date(messageData.Timestamp || messageData.timestamp || Date.now())
      };
      this.messageHandlers.forEach(handler => handler(message));
    });

    this.connection.on('InterviewStatusChanged', (status: string) => {
      this.statusHandlers.forEach(handler => handler(status));
    });

    this.connection.on('InterviewCompleted', (extractedData: string) => {
      try {
        const data: ContentPlanFormData = JSON.parse(extractedData);
        this.completionHandlers.forEach(handler => handler(data));
        this.clearInterviewId();
      } catch (error) {
        console.error('Ошибка парсинга данных интервью:', error);
      }
    });

    this.connection.onreconnecting(() => {
      console.log('Переподключение к ContentPlanInterviewHub...');
    });

    this.connection.onreconnected(() => {
      console.log('Переподключено к ContentPlanInterviewHub');
      this.reconnectAttempts = 0;
    });

    this.connection.onclose(async (error) => {
      console.error('Соединение с ContentPlanInterviewHub закрыто:', error);
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        this.reconnectAttempts++;
        setTimeout(() => this.connect(), 5000 * this.reconnectAttempts);
      }
    });
  }

  // Методы для реализации BaseInterviewService интерфейса
  async connect(): Promise<void> {
    if (!this.connection) return;

    try {
      if (this.connection.state === signalR.HubConnectionState.Disconnected) {
        await this.connection.start();
        console.log('Подключено к ContentPlanInterviewHub');
      }
    } catch (error) {
      console.error('Ошибка подключения к ContentPlanInterviewHub:', error);
      throw error;
    }
  }

  disconnect(): void {
    if (this.connection && this.connection.state !== signalR.HubConnectionState.Disconnected) {
      this.connection.stop();
      console.log('Отключено от ContentPlanInterviewHub');
    }
  }

  async startInterview(): Promise<void> {
    return this.startContentPlanInterview();
  }

  async sendMessage(interviewId: string, message: string): Promise<void> {
    return this.sendContentPlanMessage(interviewId, message);
  }

  async completeInterview(interviewId: string): Promise<void> {
    return this.completeContentPlanInterview(interviewId);
  }

  async cancelInterview(interviewId: string): Promise<void> {
    return this.cancelContentPlanInterview(interviewId);
  }

  async loadActiveInterview(): Promise<string | null> {
    return this.loadActiveContentPlanInterview();
  }

  async loadInterviewHistory(interviewId: string): Promise<InterviewMessage[]> {
    return this.loadContentPlanInterviewHistory(interviewId);
  }

  onMessage(handler: (message: InterviewMessage) => void): () => void {
    this.messageHandlers.push(handler);
    return () => {
      const index = this.messageHandlers.indexOf(handler);
      if (index > -1) {
        this.messageHandlers.splice(index, 1);
      }
    };
  }

  onStatusChange(handler: (status: string) => void): () => void {
    this.statusHandlers.push(handler);
    return () => {
      const index = this.statusHandlers.indexOf(handler);
      if (index > -1) {
        this.statusHandlers.splice(index, 1);
      }
    };
  }

  onInterviewCompleted(handler: (data: ContentPlanFormData) => void): () => void {
    this.completionHandlers.push(handler);
    return () => {
      const index = this.completionHandlers.indexOf(handler);
      if (index > -1) {
        this.completionHandlers.splice(index, 1);
      }
    };
  }

  getCurrentInterviewId(): string | null {
    return this.interviewId;
  }

  getInterviewId(): string | null {
    return this.interviewId;
  }

  // Специфичные методы для контент-плана
  async startContentPlanInterview(): Promise<void> {
    if (!this.connection) throw new Error('Соединение не установлено');

    try {
      const response = await this.connection.invoke('StartContentPlanInterview');
      this.interviewId = response;
      this.saveInterviewId(response);
      console.log('Интервью контент-плана начато:', response);
    } catch (error) {
      console.error('Ошибка при начале интервью контент-плана:', error);
      throw error;
    }
  }

  async sendContentPlanMessage(interviewId: string, message: string): Promise<void> {
    if (!this.connection) throw new Error('Соединение не установлено');

    try {
      await this.connection.invoke('SendMessage', interviewId, message);
    } catch (error) {
      console.error('Ошибка при отправке сообщения:', error);
      throw error;
    }
  }

  async completeContentPlanInterview(interviewId: string): Promise<void> {
    if (!this.connection) throw new Error('Соединение не установлено');

    try {
      await this.connection.invoke('CompleteInterview', interviewId);
    } catch (error) {
      console.error('Ошибка при завершении интервью:', error);
      throw error;
    }
  }

  async cancelContentPlanInterview(interviewId: string): Promise<void> {
    if (!this.connection) throw new Error('Соединение не установлено');

    try {
      await this.connection.invoke('CancelInterview', interviewId);
      this.clearInterviewId();
    } catch (error) {
      console.error('Ошибка при отмене интервью:', error);
      throw error;
    }
  }

  async loadActiveContentPlanInterview(): Promise<string | null> {
    // Сначала проверяем localStorage
    const savedId = localStorage.getItem(this.STORAGE_KEY);
    if (savedId) {
      this.interviewId = savedId;
      return savedId;
    }

    if (!this.connection) return null;

    try {
      const activeInterview = await this.connection.invoke('GetActiveInterview');
      if (activeInterview) {
        this.interviewId = activeInterview;
        this.saveInterviewId(activeInterview);
      }
      return activeInterview;
    } catch (error) {
      console.error('Ошибка при загрузке активного интервью:', error);
      return null;
    }
  }

  async loadContentPlanInterviewHistory(interviewId: string): Promise<InterviewMessage[]> {
    if (!this.connection) throw new Error('Соединение не установлено');

    try {
      const messagesData = await this.connection.invoke('GetInterviewHistory', interviewId);
      if (Array.isArray(messagesData)) {
        return messagesData.map((m: any) => ({
          id: m.Id || m.id || Date.now().toString(),
          content: m.Content || m.content || '',
          isFromAI: m.IsFromAI !== undefined ? m.IsFromAI : m.isFromAI || false,
          timestamp: new Date(m.Timestamp || m.timestamp || Date.now())
        }));
      }
      return [];
    } catch (error) {
      console.error('Ошибка при загрузке истории интервью:', error);
      return [];
    }
  }

  private saveInterviewId(id: string): void {
    localStorage.setItem(this.STORAGE_KEY, id);
  }

  private clearInterviewId(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    this.interviewId = null;
  }

  resetInterviewId(): void {
    this.clearInterviewId();
  }
} 